import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useSplitCollapsed, useSplitStore } from "feat/nativeNavigation/split.store";
import { useMemo, useSyncExternalStore } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { routes } from "routing";
import { allRouteNodes } from "routing/nodes";
import { isSheetPresentation } from "routing/route";
import { useHomeTab, useHomeTabStore } from "state/itineraryState/homeTab.store";
import type { THomeTab } from "state/itineraryState/homeTabs";
import { useActiveNavBar, useNavBarStore } from "./tab-bar/navBar.store";
import { TAB_BAR_HIDDEN_PATHS_REGEX } from "./tab-bar/tab-utils";
import { resolveTabForPath, TABS, type TabId } from "./tab-bar/tabs";
import { TWO_PANE_DEFAULT, useTwoPaneSetting, useTwoPaneSettingStore } from "./two-pane-setting.store";

/** The widest the app content column gets: a large phone in portrait. */
export const PHONE_MAX_WIDTH = 440;

/** Width of the tab rail. */
export const TAB_RAIL_WIDTH = 88;

/** The window width from which a full phone column and the tab rail fit side by side. */
export const RAIL_MIN_WIDTH = PHONE_MAX_WIDTH + TAB_RAIL_WIDTH;

/** The narrowest the leading pane gets. */
export const LEADING_PANE_MIN_WIDTH = 320;

/** The window width from which the leading pane, a full phone-width trailing pane and the rail fit. */
export const TWO_PANE_MIN_WIDTH = LEADING_PANE_MIN_WIDTH + PHONE_MAX_WIDTH + TAB_RAIL_WIDTH;

/** The same for a window taller than it is wide, which keeps the tab bar at the bottom: two panes. */
export const TWO_PANE_UPRIGHT_MIN_WIDTH = LEADING_PANE_MIN_WIDTH * 2;

/** The widest the trailing pane gets; the leading pane stops at PHONE_MAX_WIDTH. */
export const TRAILING_PANE_MAX_WIDTH = 700;

/** The widest the content column gets in the two-pane layout: both panes at their widest. */
export const TWO_PANE_COLUMN_MAX_WIDTH = PHONE_MAX_WIDTH + TRAILING_PANE_MAX_WIDTH;

/**
 * The width of a sheet, or of a screen drawn as a card: a comfortable share of the space it is
 * centred in, never narrower than a phone nor wider than the trailing pane gets.
 */
export const SHEET_WIDTH = `min(100%, clamp(${PHONE_MAX_WIDTH}px, 60%, ${TRAILING_PANE_MAX_WIDTH}px))`;

/**
 * Whether the app is capped at PHONE_MAX_WIDTH. iOS native navigation is not: its columns are
 * set by the native split, and it caps one-column screens at `NATIVE_ONE_COLUMN_MAX_WIDTH` instead.
 */
export const isWidthCapped = !isIosNativeNavigation();

/**
 * The widest an iOS native one-column screen gets, centred in the window from there; the measure
 * the web trailing pane caps at. Never applies inside a pane, which is as wide as its column is.
 */
export const NATIVE_ONE_COLUMN_MAX_WIDTH = TRAILING_PANE_MAX_WIDTH;

// The content column's width, on :root so portalled dialogs and drawers can read it too. Under
// native navigation the module does not know the view's column, so `routes.tsx` narrows this to
// `NATIVE_ONE_COLUMN_MAX_WIDTH` for a one-column screen once the view's pane side is known.
document.documentElement.style.setProperty("--app-width", isWidthCapped ? `min(100vw, ${PHONE_MAX_WIDTH}px)` : "100vw");

export type ScreenLayout = "compact" | "rail" | "two-pane";

/** A tab on the bar or rail: the bottom bar's tabs, plus Summary and Map in the two-pane layout. */
export type LayoutTabId = TabId | "summary" | "map";

/** What the leading pane can hold: a tab's list, or the edit-itinerary menu. */
export type PaneGroupId = TabId | "edit";

/** A home tab the tab rail carries in place of Home. */
export type HomeRailTab = "summary" | "map";

/**
 * What the trailing pane shows: a home tab, a placeholder asking you to choose, nothing, the
 * Profile, or the route itself (an entry chosen in the leading pane).
 */
export type TrailingPane = HomeRailTab | "placeholder" | "blank" | "profile" | "route";

export interface ScreenLayoutInput {
  /** Window width in CSS px. */
  width: number;
  /** Window height in CSS px. */
  height?: number;
  /** The two-pane setting for this device. */
  twoPaneSetting?: boolean;
  pathname?: string;
  /** The tab the current route belongs to. */
  activeTab?: TabId;
  iosNativeNavigation?: boolean;
  /**
   * Under iOS native navigation, whether the pane tabs' native splits show one column. UIKit
   * collapses them by size class, so on iOS this, not the width, decides two panes.
   */
  splitCollapsed?: boolean;
  homeTab?: THomeTab;
}

/** Where the tabs are drawn: along the bottom, or as the tab rail down one side of the window. */
export type TabBarPlacement = "bottom" | "rail";

export interface ScreenLayoutResolution {
  layout: ScreenLayout;
  tabBar: TabBarPlacement;
  /** The tabs in the order the bar draws them: left to right, or top to bottom on the rail. */
  tabs: readonly LayoutTabId[];
  highlightedTab?: LayoutTabId;
  /** The tab, or edit, whose list is the leading pane. */
  leadingPane?: PaneGroupId;
  /** The leading pane shows the route itself, a list to choose from, rather than the tab's list. */
  leadingShowsRoute?: true;
  trailingPane?: TrailingPane;
  /** The route is a sheet over the panes, which show the leading tab's own content. */
  sheet?: boolean;
  /** No tab bar or rail is drawn. */
  hidesTabBar?: true;
  /** The route is drawn alone, as a card centred in the window the way a sheet is over the panes. */
  card?: true;
  /** The route is drawn alone across the whole two-pane column, up to the gutters. */
  wide?: true;
}

const BOTTOM_BAR_TABS = TABS.map((tab) => tab.id);

// Top to bottom, the bottom bar's order reversed.
const RAIL_TABS = [...BOTTOM_BAR_TABS].reverse();

// Left to right, the bottom bar's order with Summary and Map in place of Home.
const TWO_PANE_BOTTOM_TABS: readonly LayoutTabId[] = ["summary", "map", "chat", "team", "account"];

// Top to bottom, the two-pane bottom bar's order reversed, so Summary is bottom-most.
const TWO_PANE_TABS = [...TWO_PANE_BOTTOM_TABS].reverse();

/** The item viewer routes. */
export const ITEM_ROUTE_PATHS: readonly string[] = Object.values(routes.item).map((node) => node.path);

interface PaneRoutes {
  /** The tab root, and what the trailing pane shows there. */
  root: { path: string; trailing: Exclude<TrailingPane, "route"> };
  /** Routes shown in the trailing pane beside the tab's list. */
  entries: readonly string[];
  /** Lists opened from the tab, shown in the leading pane with the trailing pane asking for a choice. */
  lists?: readonly string[];
}

const { edit } = routes;

// Expenses show as a sheet over the panes, as Alerts does, rather than in editing's panes.
const EXPENSE_PATHS = Object.values(edit)
  .map((node) => node.path)
  .filter((path) => path.startsWith(edit.expenses.path));

// The itineraries list, opened from the tab bar's menu, shows as a sheet over the panes too.
const ITINERARIES_PATHS = [
  routes.itineraries.path,
  routes.otherItineraries.list.path,
  routes.otherItineraries.tag.path,
];

const PANE_ROUTES: Partial<Record<PaneGroupId, PaneRoutes>> = {
  home: {
    root: { path: routes.home.path, trailing: "summary" },
    entries: [...ITEM_ROUTE_PATHS, routes.notes.item.path, routes.notes.edit.path],
    lists: [
      routes.section.items.path,
      routes.section.categoryItems.path,
      routes.favourites.path,
      routes.notes.list.path,
    ],
  },
  chat: {
    root: { path: routes.chat.list.path, trailing: "placeholder" },
    entries: [
      routes.chat.room.path,
      routes.chat.info.path,
      routes.chat.members.path,
      routes.chat.notifications.path,
      routes.chat.manage.path,
    ],
  },
  team: {
    root: { path: routes.team.page.path, trailing: "placeholder" },
    entries: [routes.team.search.path, routes.team.categories.path, routes.team.member.path],
  },
  account: {
    root: { path: routes.account.page.path, trailing: "profile" },
    // Profile Edit and its picker included: they replace Profile in the trailing pane.
    entries: allRouteNodes
      .filter((node) => node.meta.tab === "account" && node.path !== routes.account.page.path)
      .map((node) => node.path),
  },
  // The edit menu always leads; every screen chosen from it, lists included, trails.
  edit: {
    root: { path: edit.sectionSelection.path, trailing: "blank" },
    entries: Object.values(edit)
      .map((node) => node.path)
      .filter((path) => path !== edit.sectionSelection.path && !EXPENSE_PATHS.includes(path)),
  },
};

/** The root route of a tab, or of edit, shown in two panes. */
export const paneRootPath = (group: PaneGroupId): string | undefined => PANE_ROUTES[group]?.root.path;

/** What the trailing pane shows at a tab's root, or at edit's, before anything is chosen. */
export const paneRootTrailing = (group: PaneGroupId): Exclude<TrailingPane, "route"> | undefined =>
  PANE_ROUTES[group]?.root.trailing;

/** The tabs and groups that show in two panes. */
export const PANE_GROUP_IDS = Object.keys(PANE_ROUTES) as PaneGroupId[];

/** The tab or edit whose list leads, and what trails, for a route shown in two panes. */
function findPane(pathname: string): { tab: PaneGroupId; trailing: TrailingPane; list?: true } | undefined {
  for (const [tab, pane] of Object.entries(PANE_ROUTES) as [PaneGroupId, PaneRoutes][]) {
    if (pathname === pane.root.path) return { tab, trailing: pane.root.trailing };
    if (pane.entries.some((path) => matchPath(path, pathname))) return { tab, trailing: "route" };
    if (pane.lists?.some((path) => matchPath(path, pathname))) return { tab, trailing: "placeholder", list: true };
  }
  return undefined;
}

// Flows that take the whole window as one column, with no rail, wherever two panes are allowed.
const FULL_WINDOW_PATHS = [
  /^\/$/,
  /^\/auth(\/.*)?$/i,
  /^\/authentication(\/.*)?$/i,
  /^\/dev(\/.*)?$/i,
  /^\/create-itinerary$/i,
];

// Of those, the screens drawn across the two panes' column.
const WIDE_PATHS = [/^\/create-itinerary$/i];

// Of those, the welcome, sign-in and sign-up screens, drawn as a card.
const CARD_PATHS = [/^\/$/, /^\/auth(\/.*)?$/i, /^\/authentication(\/.*)?$/i];

const matchesAny = (pathname: string, patterns: readonly RegExp[]) => patterns.some((regex) => regex.test(pathname));

// A sheet route, or a route inside a sheet's stack.
const SHEET_PATTERNS = allRouteNodes
  .filter((node) => isSheetPresentation(node.meta.present))
  .flatMap((node) => [node.path, ...(node.meta.modalMatch ?? []).map((pattern) => pattern.replace("*rest", "*"))])
  .concat(EXPENSE_PATHS, ITINERARIES_PATHS);

/** A sheet route and the tab it belongs to, if any; null when the route is not a sheet. */
function findSheet(pathname: string): { tab?: TabId } | null {
  if (!SHEET_PATTERNS.some((pattern) => matchPath(pattern, pathname))) return null;
  return { tab: allRouteNodes.find((node) => matchPath(node.path, pathname))?.meta.tab };
}

/**
 * Whether a window keeps the tab bar at the bottom and splits into halves: taller than it is wide,
 * or close to square and narrower than two panes beside the rail, like an open foldable.
 */
const isUpright = (width: number, height: number) =>
  height > width || (width < TWO_PANE_MIN_WIDTH && height > width * 0.8);

export function resolveScreenLayout({
  width,
  height = 0,
  twoPaneSetting = TWO_PANE_DEFAULT,
  pathname = "",
  activeTab,
  iosNativeNavigation = false,
  splitCollapsed = true,
  homeTab = "itinerary",
}: ScreenLayoutInput): ScreenLayoutResolution {
  const upright = isUpright(width, height);
  // iOS native navigation draws its own tab bar outside the web view, so the web view never
  // reserves room for the rail nor moves controls onto it.
  const tabBar: TabBarPlacement = !upright && width >= RAIL_MIN_WIDTH && !iosNativeNavigation ? "rail" : "bottom";
  const oneColumn: ScreenLayout = tabBar === "rail" ? "rail" : "compact";
  const twoPaneMinWidth = upright ? TWO_PANE_UPRIGHT_MIN_WIDTH : TWO_PANE_MIN_WIDTH;

  const roomForTwoPanes = iosNativeNavigation ? !splitCollapsed : width >= twoPaneMinWidth;

  if (roomForTwoPanes && twoPaneSetting) {
    const tabs = tabBar === "rail" ? TWO_PANE_TABS : TWO_PANE_BOTTOM_TABS;
    // Summary unless Map is chosen; Itinerary is the leading pane.
    const homeRailTab: HomeRailTab = homeTab === "map" ? "map" : "summary";
    const railTab = (tab: TabId | undefined) => (tab === "home" ? homeRailTab : tab);

    if (matchesAny(pathname, FULL_WINDOW_PATHS)) {
      const card = matchesAny(pathname, CARD_PATHS) ? { card: true as const } : {};
      const wide = matchesAny(pathname, WIDE_PATHS) ? { wide: true as const } : {};
      return {
        layout: oneColumn,
        tabBar,
        tabs,
        highlightedTab: railTab(activeTab),
        hidesTabBar: true,
        ...card,
        ...wide,
      };
    }

    const twoPane = (
      tab: PaneGroupId,
      trailing: TrailingPane,
      extra?: { sheet?: true; list?: true },
    ): ScreenLayoutResolution => ({
      layout: "two-pane",
      tabBar,
      tabs,
      // An item or a list highlights Summary, whichever home tab was chosen before.
      highlightedTab:
        tab === "home" && (trailing === "route" || extra?.list) ? "summary" : railTab(tab === "edit" ? activeTab : tab),
      leadingPane: tab,
      trailingPane: trailing === "summary" ? homeRailTab : trailing,
      ...(extra?.sheet && { sheet: true as const }),
      ...(extra?.list && { leadingShowsRoute: true as const }),
      // Editing takes the whole window, with no rail.
      ...(tab === "edit" && { hidesTabBar: true as const }),
    });

    const pane = findPane(pathname);
    if (pane) return twoPane(pane.tab, pane.trailing, { list: pane.list });

    const sheet = findSheet(pathname);
    if (sheet) {
      const tab = sheet.tab ?? activeTab ?? "home";
      return twoPane(tab, PANE_ROUTES[tab]?.root.trailing ?? "summary", { sheet: true });
    }
    // Every other route shows in one column.
    return { layout: oneColumn, tabBar, tabs, highlightedTab: railTab(activeTab) };
  }
  const hidesTabBar = matchesAny(pathname, TAB_BAR_HIDDEN_PATHS_REGEX) ? { hidesTabBar: true as const } : {};
  const tabs = tabBar === "rail" ? RAIL_TABS : BOTTOM_BAR_TABS;
  return { layout: oneColumn, tabBar, tabs, highlightedTab: activeTab, ...hidesTabBar };
}

// The breakpoints the layout changes at, so resizing between them re-renders nothing.
const WIDTH_STEPS = [RAIL_MIN_WIDTH, TWO_PANE_UPRIGHT_MIN_WIDTH, TWO_PANE_MIN_WIDTH] as const;

const subscribe = (onChange: () => void) => {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
};

/** The window's size rounded down to the breakpoint below it, keeping which way up it is. */
const currentWindowClass = () => {
  const width = [...WIDTH_STEPS].reverse().find((step) => window.innerWidth >= step) ?? 0;
  const upright = isUpright(window.innerWidth, window.innerHeight);
  return `${width}:${upright}`;
};

/** Whether the current window and route show two panes. */
export const useIsTwoPane = () => useScreenLayout().layout === "two-pane";

/** The screen layout for the current window, route and two-pane setting. */
export function useScreenLayout(): ScreenLayoutResolution {
  const [widthStep, upright] = useSyncExternalStore(subscribe, currentWindowClass).split(":");
  const width = Number(widthStep);
  // A stand-in height on the same side of the width as the window's.
  const height = upright === "true" ? width + 1 : 0;
  const { pathname } = useLocation();
  const activeNavBar = useActiveNavBar();
  const homeTab = useHomeTab();
  const twoPaneSetting = useTwoPaneSetting();
  const splitCollapsed = useSplitCollapsed();
  // An item route keeps the tab it was opened from, which NavBar tracks.
  const activeTab = resolveTabForPath(pathname)?.id ?? activeNavBar;

  return useMemo(
    () =>
      resolveScreenLayout({
        width,
        height,
        twoPaneSetting,
        pathname,
        activeTab,
        homeTab,
        iosNativeNavigation: isIosNativeNavigation(),
        splitCollapsed,
      }),
    [width, height, twoPaneSetting, pathname, activeTab, homeTab, splitCollapsed],
  );
}

/** The screen layout now, for `pathname` or the current route, read outside render. */
export function resolveCurrentScreenLayout(pathname = window.location.pathname): ScreenLayoutResolution {
  return resolveScreenLayout({
    width: window.innerWidth,
    height: window.innerHeight,
    twoPaneSetting: useTwoPaneSettingStore.getState().twoPaneSetting,
    pathname,
    activeTab: resolveTabForPath(pathname)?.id ?? useNavBarStore.getState().activeNavBar,
    homeTab: useHomeTabStore.getState().homeTab,
    iosNativeNavigation: isIosNativeNavigation(),
    splitCollapsed: useSplitStore.getState().collapsed,
  });
}
